/* eslint-disable jsx-a11y/media-has-caption */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { format } from 'date-fns';
import { styled } from '@mui/material/styles';
import { Box, Typography, Avatar, Paper, IconButton, Popover, Menu, MenuItem } from '@mui/material';
import data from '@emoji-mart/data';
import Picker from '@emoji-mart/react';
import { useDispatch } from '../../redux/store';
import { updateChatPin } from '../../redux/slices/internalChat';
import axios from '../../utils/axios';
import Iconify from '../../components/Iconify';
import Image from '../../components/Image';
import { HOST_API } from '../../config';
import MessageReceiptsModal from './MessageReceiptsModal';

// ----------------------------------------------------------------------

const RootStyle = styled('div')(({ theme }) => ({
    display: 'flex',
    marginBottom: theme.spacing(3),
}));

const ContentStyle = styled(Paper, {
    shouldForwardProp: (prop) => prop !== 'isMe',
})(({ theme, isMe }) => ({
    width: 'fit-content',
    maxWidth: 320,
    padding: theme.spacing(1.5),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: isMe ? theme.palette.primary.lighter : theme.palette.background.neutral,
    color: isMe ? theme.palette.text.primary : theme.palette.text.primary,
    wordBreak: 'break-word',
    overflowWrap: 'anywhere'
}));

const InfoStyle = styled(Typography)(({ theme }) => ({
    display: 'flex',
    marginBottom: theme.spacing(0.75),
    color: theme.palette.text.secondary,
}));

// ----------------------------------------------------------------------

const formatDuration = (seconds) => {
    if (!seconds) return '00:00';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

// ----------------------------------------------------------------------

const InternalChatMessageItem = ({ message, isMe, chatType, onOpenLightbox, participants, currentUserId, chatId, onReply }) => {
    const dispatch = useDispatch();
    const sender = message.senderId || {};
    const senderName = sender.name || 'Desconhecido';
    const senderAvatar = sender.avatar || '';
    const firstName = senderName.split(' ')[0];

    const meParticipant = participants?.find(p => String(p.userId._id || p.userId) === String(currentUserId));
    const isAdmin = meParticipant?.isAdmin;

    // Grupos: só admin pode fixar. Em conversas privadas: qualquer participante pode.
    const canPin = chatType === 'group' ? isAdmin : !!meParticipant;

    const [openReceipts, setOpenReceipts] = useState(false);

    // ---- Unified Context Menu ----
    const [menuAnchorEl, setMenuAnchorEl] = useState(null);
    const openMenu = Boolean(menuAnchorEl);

    const handleOpenMenu = (event) => {
        setMenuAnchorEl(event.currentTarget);
    };

    const handleCloseMenu = () => {
        setMenuAnchorEl(null);
    };

    // ---- Emoji Picker (aberto a partir do menu) ----
    const [emojiAnchorEl, setEmojiAnchorEl] = useState(null);
    const openEmoji = Boolean(emojiAnchorEl);

    const handleOpenEmoji = (event) => {
        handleCloseMenu(); // fecha o menu antes de abrir o picker
        setEmojiAnchorEl(event.currentTarget);
    };

    const handleCloseEmoji = () => {
        setEmojiAnchorEl(null);
    };

    const handleEmojiSelect = async (emoji) => {
        handleCloseEmoji();
        try {
            await axios.post(`/api/internal-chat/${chatId}/messages/${message._id}/reaction`, {
                emoji: emoji.native
            });
            // State update is handled by socket event -> Redux
        } catch (error) {
            console.error('Failed to add reaction:', error);
        }
    };

    const handleReactionClick = async (emoji) => {
        try {
            await axios.post(`/api/internal-chat/${chatId}/messages/${message._id}/reaction`, {
                emoji
            });
        } catch (error) {
            console.error('Failed to toggle reaction:', error);
        }
    };

    // ---- Responder ----
    const handleReply = () => {
        handleCloseMenu();
        if (onReply) onReply(message);
    };

    // ---- Fixar Mensagem ----
    const handlePinMessage = async () => {
        handleCloseMenu();
        try {
            const response = await axios.put(`/api/internal-chat/${chatId}/pin`, {
                messageId: message._id
            });
            // O backend retorna chat.pinnedMessage diretamente em response.data
            // Fazemos dispatch local como fallback caso o socket não propague o evento
            dispatch(updateChatPin({
                chatId,
                pinnedMessage: response.data || { messageId: message._id }
            }));
        } catch (error) {
            console.error('Failed to pin message:', error);
        }
    };

    // ---- Render Attachment ----
    const renderAttachment = (att, index) => {
        const accessToken = window.localStorage.getItem('accessToken');
        const fileUrl = `${HOST_API}${att.url}?token=${accessToken}`;

        if (att.type === 'image') {
            return (
                <Box key={index} sx={{ p: 0, cursor: 'pointer', '&:hover': { opacity: 0.8 } }} onClick={() => onOpenLightbox(fileUrl)}>
                    <Image
                        alt="attachment"
                        src={fileUrl}
                        sx={{
                            borderRadius: 1,
                            maxHeight: 300,
                            minWidth: 150,
                        }}
                        ratio="1/1"
                    />
                </Box>
            );
        }

        if (att.type === 'video') {
            return (
                <Box key={index} sx={{ mt: 1, mb: 1 }}>
                    <video
                        src={fileUrl}
                        controls
                        style={{
                            maxWidth: '100%',
                            borderRadius: '8px',
                            maxHeight: 300,
                            backgroundColor: '#000'
                        }}
                    />
                </Box>
            );
        }

        if (att.type === 'audio') {
            return (
                <Box key={index} sx={{ mt: 1, mb: 1, minWidth: 240 }}>
                    <audio
                        controls
                        src={fileUrl}
                        style={{ width: '100%' }}
                    />
                </Box>
            );
        }

        return (
            <Box
                key={index}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    mt: 1,
                    mb: 1,
                    p: 1.5,
                    borderRadius: 1,
                    bgcolor: 'background.paper',
                    border: (theme) => `1px solid ${theme.palette.divider}`,
                    cursor: 'pointer',
                    '&:hover': { bgcolor: 'action.hover' }
                }}
                onClick={() => window.open(fileUrl, '_blank')}
            >
                <div>
                    <Iconify icon="eva:file-text-fill" width={28} height={28} sx={{ color: 'text.secondary', mr: 1 }} />
                </div>
                <Box sx={{ overflow: 'hidden', mr: 1 }}>
                    <Typography variant="subtitle2" noWrap>
                        {att.name || 'Arquivo'}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                        {att.size ? `${(att.size / 1024).toFixed(1)} KB` : 'Download'}
                    </Typography>
                </Box>
                <Iconify icon="eva:download-outline" width={24} height={24} sx={{ color: 'text.disabled', ml: 'auto' }} />
            </Box>
        );
    };

    return (
        <RootStyle>
            <Box
                sx={{
                    display: 'flex',
                    ...(isMe && {
                        ml: 'auto',
                    }),
                }}
            >
                {/* Avatar apenas se não for Eu e for Grupo */}
                {!isMe && chatType === 'group' && (
                    <Avatar
                        alt={senderName}
                        src={senderAvatar ? `${HOST_API}${senderAvatar}` : ''}
                        sx={{ width: 32, height: 32, mr: 2 }}
                    />
                )}

                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: isMe ? 'flex-end' : 'flex-start' }}>
                    <InfoStyle
                        variant="caption"
                        sx={{
                            ...(isMe && { justifyContent: 'flex-end' }),
                        }}
                    >
                        {!isMe && `${firstName},`}&nbsp;
                        {isMe && 'Eu,'}&nbsp;
                        {format(new Date(message.timestamp), 'dd/MM/yyyy HH:mm')}
                    </InfoStyle>

                    {/* Bubble + Context Trigger */}
                    <Box sx={{ position: 'relative', '&:hover .msg-actions': { opacity: 1 } }}>
                        <ContentStyle isMe={isMe}>
                            {/* Quoted Message */}
                            {message.replyTo && (
                                <Box sx={{
                                    bgcolor: 'rgba(0,0,0,0.1)',
                                    p: 1,
                                    borderRadius: 1,
                                    mb: 1,
                                    borderLeft: '4px solid #000',
                                    opacity: 0.8
                                }}>
                                    <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block' }}>
                                        {message.replyTo.senderName}
                                    </Typography>
                                    <Typography variant="caption" noWrap>
                                        {message.replyTo.content}
                                    </Typography>
                                </Box>
                            )}

                            {/* Attachments */}
                            {message.attachments?.length > 0 && (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                    {message.attachments.map((att, index) => renderAttachment(att, index))}
                                </Box>
                            )}

                            {/* Content Text */}
                            {message.type === 'text' && message.content && (
                                <Typography variant="body2" sx={{ mt: message.attachments?.length ? 1 : 0, whiteSpace: 'pre-wrap' }}>
                                    {message.content}
                                </Typography>
                            )}

                            {/* Call Logs */}
                            {message.type === 'call_log' && message.callData && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 160, p: 0.5 }}>
                                    <Box sx={{
                                        width: 40,
                                        height: 40,
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        bgcolor: message.callData.status === 'completed'
                                            ? 'success.lighter'
                                            : 'error.lighter'
                                    }}>
                                        <Iconify
                                            icon={message.callData.type === 'video' ? 'eva:video-fill' : 'eva:phone-call-fill'}
                                            sx={{
                                                color: message.callData.status === 'completed' ? 'success.main' : 'error.main',
                                                width: 24, height: 24
                                            }}
                                        />
                                    </Box>
                                    <Box>
                                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                            {message.callData.status === 'completed' && `Chamada de ${message.callData.type === 'video' ? 'Vídeo' : 'Voz'}`}
                                            {message.callData.status === 'missed' && `Chamada Perdida`}
                                            {message.callData.status === 'rejected' && `Chamada Recusada`}
                                            {message.callData.status === 'busy' && `Linha Ocupada`}
                                        </Typography>
                                        {message.callData.status === 'completed' && message.callData.duration > 0 && (
                                            <Typography variant="caption" sx={{ opacity: 0.7, display: 'block' }}>
                                                Duração: {formatDuration(message.callData.duration)}
                                            </Typography>
                                        )}
                                    </Box>
                                </Box>
                            )}
                        </ContentStyle>

                        {/* Botão único de menu de contexto (aparece no hover) */}
                        <Box
                            className="msg-actions"
                            sx={{
                                position: 'absolute',
                                top: '50%',
                                transform: 'translateY(-50%)',
                                opacity: 0,
                                transition: 'opacity 0.2s',
                                [isMe ? 'left' : 'right']: -36,
                            }}
                        >
                            <IconButton
                                size="small"
                                onClick={handleOpenMenu}
                                sx={{ bgcolor: 'background.paper', boxShadow: 1, '&:hover': { bgcolor: 'background.paper' } }}
                            >
                                <Iconify icon="eva:more-vertical-fill" width={18} height={18} sx={{ color: 'text.secondary' }} />
                            </IconButton>
                        </Box>

                        {/* Menu de Contexto Unificado */}
                        <Menu
                            anchorEl={menuAnchorEl}
                            open={openMenu}
                            onClose={handleCloseMenu}
                            anchorOrigin={{ vertical: 'center', horizontal: isMe ? 'left' : 'right' }}
                            transformOrigin={{ vertical: 'center', horizontal: isMe ? 'right' : 'left' }}
                        >
                            {/* Responder */}
                            <MenuItem onClick={handleReply}>
                                <Iconify icon="eva:undo-fill" sx={{ mr: 1.5, width: 20, height: 20, color: 'text.secondary' }} />
                                Responder
                            </MenuItem>

                            {/* Reagir com Emoji */}
                            <MenuItem onClick={handleOpenEmoji}>
                                <Iconify icon="eva:smiling-face-fill" sx={{ mr: 1.5, width: 20, height: 20, color: 'text.secondary' }} />
                                Reagir
                            </MenuItem>

                            {/* Fixar Mensagem (grupos: só admin; privadas: qualquer um) */}
                            {canPin && (
                                <MenuItem onClick={handlePinMessage}>
                                    <Iconify icon="eva:pin-fill" sx={{ mr: 1.5, width: 20, height: 20, color: 'text.secondary' }} />
                                    Fixar Mensagem
                                </MenuItem>
                            )}

                            {/* Recibos de Leitura (somente minahs mensagens) */}
                            {isMe && (
                                <MenuItem onClick={() => { handleCloseMenu(); setOpenReceipts(true); }}>
                                    <Iconify icon="eva:done-all-fill" sx={{ mr: 1.5, width: 20, height: 20, color: 'text.secondary' }} />
                                    Ver recibos de leitura
                                </MenuItem>
                            )}
                        </Menu>

                        {/* Receipts Modal */}
                        {isMe && openReceipts && (
                            <MessageReceiptsModal
                                open={openReceipts}
                                onClose={() => setOpenReceipts(false)}
                                chatId={chatId}
                                messageId={message._id}
                            />
                        )}

                        {/* Emoji Picker (abre após fechar o menu) */}
                        <Popover
                            open={openEmoji}
                            anchorEl={emojiAnchorEl}
                            onClose={handleCloseEmoji}
                            anchorOrigin={{
                                vertical: 'top',
                                horizontal: isMe ? 'left' : 'right',
                            }}
                            transformOrigin={{
                                vertical: 'bottom',
                                horizontal: isMe ? 'right' : 'left',
                            }}
                        >
                            <Picker
                                data={data}
                                onEmojiSelect={handleEmojiSelect}
                                theme="light"
                                locale="pt"
                            />
                        </Popover>
                    </Box>

                    {/* Reactions Display */}
                    {message.reactions && message.reactions.length > 0 && (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5, justifyContent: isMe ? 'flex-end' : 'flex-start' }}>
                            {message.reactions.map((reaction, index) => (
                                <Box
                                    key={index}
                                    onClick={() => handleReactionClick(reaction.emoji)}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        bgcolor: 'background.paper',
                                        borderRadius: 2,
                                        px: 0.5,
                                        py: 0.25,
                                        cursor: 'pointer',
                                        boxShadow: 1,
                                        border: (theme) => `1px solid ${reaction.by.includes(currentUserId) ? theme.palette.primary.main : 'transparent'}`,
                                        '&:hover': { bgcolor: 'action.hover' }
                                    }}
                                >
                                    <Typography variant="caption" sx={{ mr: 0.5 }}>{reaction.emoji}</Typography>
                                    <Typography variant="caption" sx={{ fontWeight: 'bold' }}>{reaction.by.length}</Typography>
                                </Box>
                            ))}
                        </Box>
                    )}

                    {/* Read/Delivered Status (only for my messages) */}
                    {isMe && currentUserId && participants && (
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 0.5, width: '100%' }}>
                            <Iconify
                                icon={
                                    participants
                                        .filter(p => {
                                            const pId = p.userId._id || p.userId;
                                            return String(pId) !== String(currentUserId);
                                        })
                                        .every(p => p.lastReadAt && new Date(p.lastReadAt) >= new Date(message.timestamp))
                                        ? "eva:checkmark-circle-2-fill"
                                        : "eva:checkmark-circle-2-outline"
                                }
                                width={16}
                                height={16}
                                sx={{
                                    color: participants
                                        .filter(p => {
                                            const pId = p.userId._id || p.userId;
                                            return String(pId) !== String(currentUserId);
                                        })
                                        .every(p => p.lastReadAt && new Date(p.lastReadAt) >= new Date(message.timestamp))
                                        ? 'primary.main'
                                        : 'text.disabled'
                                }}
                            />
                        </Box>
                    )}
                </Box>
            </Box>
        </RootStyle>
    );
}

InternalChatMessageItem.propTypes = {
    message: PropTypes.object.isRequired,
    isMe: PropTypes.bool.isRequired,
    chatType: PropTypes.string,
    onOpenLightbox: PropTypes.func,
    participants: PropTypes.array,
    currentUserId: PropTypes.string,
    chatId: PropTypes.string,
    onReply: PropTypes.func
};

export default React.memo(InternalChatMessageItem);
